import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useKeycloak } from "@react-keycloak/web";
import { createBrainstorm, createAssetKnowledgeBase } from "../../../services/Brainstorm/BrainstormService";
import Swal from "sweetalert2";
import Container from "../../Atoms/Container";
import ConceptCard from "./Molecules/ConceptCard";
import ConceptCardSkeleton from "./Molecules/ConceptCardSkeleton";
import ConceptCardFailed from "./Molecules/ConceptCardFailed";
import ConceptDetailsModal from "./Molecules/ConceptDetailsModal";
import useBrainstormStore from "../../../hooks/useBrainstormStore";
import AnimatedBrainLoader from "../../Atoms/AnimatedBrainLoader";

import './styles.css';
import ButtonMain from "../../Atoms/ButtonMain";
import KnowledgeBaseModal from "../BrainstormIdeas/Molecules/KnowledgeBaseModal";

export default function BrainstormConceptsTemplate({
    loadingConcepts,
    hasError,
    pendingIdeaIds = new Set(),
    failedIdeas = new Map(),
    conceptsVersion = 0,
    onRetryConcept,
}) {
    const { projectId } = useParams();  
    const { keycloak } = useKeycloak();
    const navigate = useNavigate();
    const KEYCLOACK_TOKEN = keycloak.token;

    const [selectedGuidingQuestion, setSelectedGuidingQuestion] = useState(null);
    const [currentBrainstorm, setCurrentBrainstorm] = useState(null);
    const [guidingQuestions, setGuidingQuestions] = useState([]);
    const [selectedIdeas, setSelectedIdeas] = useState([]);
    const [isLoadingData, setIsLoadingData] = useState(false);
    const [loadingKnowledgeBase, setLoadingKnowledgeBase] = useState(false);
    const [selectedIdea, setSelectedIdea] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showKnowledgeBaseReviewModal, setShowKnowledgeBaseReviewModal] = useState(false);
    const { 
        getBrainstormsByProject,
        getIdeasByGuidingQuestion, 
        getGuidingQuestionsByChallenge,
        getIdeaById,
        toggleGuidingQuestionKnowledgeBase,
        clearAllBrainstorms
    } = useBrainstormStore();

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId, loadingConcepts]);

    useEffect(() => {
        if (currentBrainstorm && selectedGuidingQuestion) {
            const allIdeas = getIdeasByGuidingQuestion(currentBrainstorm.id, selectedGuidingQuestion);
            const selected = allIdeas.filter(idea => idea.selected === true);
            setSelectedIdeas(selected);
        }
        // conceptsVersion: relê o store a cada conceito recebido pelo stream
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadingConcepts, conceptsVersion, currentBrainstorm, selectedGuidingQuestion]);

    const loadData = async () => {
        setIsLoadingData(true);
        
        try {
            const currentBrainstormStore = getBrainstormsByProject(parseInt(projectId));

            if (!currentBrainstormStore || currentBrainstormStore.length === 0) {
                console.warn('No brainstorm found for project:', projectId);
                return;
            }

            const brainstorm = currentBrainstormStore[0];
            setCurrentBrainstorm(brainstorm);
            
            const selectedChallenge = brainstorm.challenges.find(challenge => challenge.selected === true);

            if (!selectedChallenge) {
                console.warn('No selected challenge found');
                return;
            }

            const challengeGuidingQuestions = getGuidingQuestionsByChallenge(brainstorm.id, selectedChallenge.id);
            const selectedGuidingQuestions = challengeGuidingQuestions.filter(question => question.selected === true);
            setGuidingQuestions(selectedGuidingQuestions);
            
            if (selectedGuidingQuestions.length === 0) {
                console.warn('No selected guiding questions found');
                return;
            }

            // Seleciona a primeira questão já durante a geração, para que o grid apareça
            // de imediato — mas só se o usuário ainda não escolheu uma, senão a releitura
            // ao fim do stream sobrescreveria a escolha dele.
            if (selectedGuidingQuestion === null) {
                const firstQuestion = selectedGuidingQuestions[0];
                setSelectedGuidingQuestion(firstQuestion.id);

                const allIdeas = getIdeasByGuidingQuestion(brainstorm.id, firstQuestion.id);
                const selected = allIdeas.filter(idea => idea.selected === true);
                setSelectedIdeas(selected);
            }
        } catch (error) {
            console.error('Error in loadData:', error);
        } finally {
            setIsLoadingData(false);
        }
    }

    const handleGuidingQuestionSelect = (question) => {
        if (!currentBrainstorm) return;
        
        setSelectedGuidingQuestion(question.id);
        
        const allIdeas = getIdeasByGuidingQuestion(currentBrainstorm.id, question.id);
        const selected = allIdeas.filter(idea => idea.selected === true);
        setSelectedIdeas(selected);
    };

    const handleConceptCardClick = (idea) => {
        if (idea?.id) {
            const latestIdea = getIdeaById(idea.id);
            setSelectedIdea(latestIdea || idea);
        } else {
            setSelectedIdea(idea);
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedIdea(null);
    };

    const handleConceptDeleted = (ideaId) => {
        if (!currentBrainstorm || !selectedGuidingQuestion) return;

        // Refresh the selected ideas list from the store
        const allIdeas = getIdeasByGuidingQuestion(currentBrainstorm.id, selectedGuidingQuestion);
        const selected = allIdeas.filter(idea => idea.selected === true);
        setSelectedIdeas(selected);

        // Close the modal if the deleted idea was the selected one
        if (selectedIdea?.id === ideaId) {
            setShowModal(false);
            setSelectedIdea(null);
        }
    };

    const handleConceptUpdated = (ideaId) => {
        if (!currentBrainstorm || !selectedGuidingQuestion) return;

        const allIdeas = getIdeasByGuidingQuestion(currentBrainstorm.id, selectedGuidingQuestion);
        const selected = allIdeas.filter(idea => idea.selected === true);
        setSelectedIdeas(selected);

        if (selectedIdea?.id === ideaId) {
            const updatedIdea = allIdeas.find(idea => idea.id === ideaId);
            if (updatedIdea) {
                setSelectedIdea(updatedIdea);
            }
        }
    };

    const handleFinishBrainstorm = () => {
        setShowKnowledgeBaseReviewModal(true);
    }

    const generateNewFileName = () => {
        const date = new Date();
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const day = date.getDate();
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const seconds = date.getSeconds();

        return 'brainstorm_' + year + month + day + '_' + hours + minutes + seconds;
    }

    const handleToggleKnowledgeBase = (brainstormId, questionId) => {
        toggleGuidingQuestionKnowledgeBase(brainstormId, questionId);
        
        const updatedBrainstorm = getBrainstormsByProject(parseInt(projectId))[0];
        if (updatedBrainstorm) {
            setCurrentBrainstorm(updatedBrainstorm);
        }
    }

    const handleFinishKnowledgeBaseReview = async () => {
        try {
            setLoadingKnowledgeBase(true);
            
            const latestBrainstormForPayload = getBrainstormsByProject(parseInt(projectId))[0];
            if (!latestBrainstormForPayload) {
                Swal.fire({
                    icon: 'error',
                    title: 'Erro ao finalizar trabalho',
                    text: 'Erro ao buscar dados atualizados. Tente novamente.',
                    timer: 2000,
                    showConfirmButton: false,
                    background: '#27272A',
                    color: '#fff',
                    position: 'top-end',
                });
                return;
            }
            
            const allBrainstormIdeas = latestBrainstormForPayload.guiding_questions.map(question => question.ideas);
            const payload = {
                challenges: latestBrainstormForPayload.challenges,
                guidingQuestions: {...latestBrainstormForPayload.guiding_questions},
                ideas: {...allBrainstormIdeas.flat()},
                groups: {...latestBrainstormForPayload.groups},
                concepts: {...latestBrainstormForPayload.concepts}
            }

            const newBrainstorm = await createBrainstorm(projectId, payload, KEYCLOACK_TOKEN);

            if (newBrainstorm) {
                const currentChallenge = latestBrainstormForPayload.challenges.find(c => c.selected === true);
                if (!currentChallenge) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Erro ao finalizar trabalho',
                        text: 'Erro ao identificar desafio principal. Tente novamente.',
                    });
                    return;
                }

                const currentGuidingQuestion = guidingQuestions.find(q => q.id === selectedGuidingQuestion);
                if (!currentGuidingQuestion) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Erro ao finalizar trabalho',
                        text: 'Erro ao identificar questão norteadora. Tente novamente.',
                    });
                    return;
                }

                const latestBrainstorm = getBrainstormsByProject(parseInt(projectId))[0];
                if (!latestBrainstorm) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Erro ao finalizar trabalho',
                        text: 'Erro ao buscar dados atualizados. Tente novamente.',
                        timer: 2000,
                        showConfirmButton: false,
                        background: '#27272A',
                        color: '#fff',
                        position: 'top-end',
                    });
                    return;
                }

                const formattedGuidingQuestions = latestBrainstorm.guiding_questions
                .filter(question => question.in_knowledge_base && question.ideas.some(idea => idea.selected))
                .map(q => ({
                    ...q,
                    ideas: q.ideas.filter(idea => idea.selected)
                }));

                const newFileName = generateNewFileName();

                const assetPayload = {
                    templateName: 'brainstorm',
                    challenge: currentChallenge.text,
                    blocks: formattedGuidingQuestions,
                    fileName: newFileName
                }

                const assetKnowledgeBase = await createAssetKnowledgeBase(projectId, assetPayload, newFileName, KEYCLOACK_TOKEN);
                if (assetKnowledgeBase) {
                    Swal.fire({
                        title: 'Trabalho finalizado!',
                        text: 'O trabalho foi finalizado com sucesso.',
                        timer: 1500,
                        showConfirmButton: false,
                        background: '#27272A',
                        color: '#fff',
                        position: 'top-end',
                    });
        
                    clearAllBrainstorms();
                    navigate(`/project/${projectId}`);
                } else {
                    Swal.fire({
                        title: 'Erro ao criar asset da base de conhecimento',
                        text: 'Ocorreu um erro ao criar o asset da base de conhecimento. Tente novamente.',
                        timer: 2000,
                        showConfirmButton: false,
                        background: '#27272A',
                        color: '#fff',
                        position: 'top-end',
                    });
                    return;
                }
            } else {
                Swal.fire({
                    title: 'Erro ao finalizar trabalho',
                    text: 'Ocorreu um erro ao concluir a finalização do trabalho. Tente novamente.',
                    timer: 2000,
                    showConfirmButton: false,
                    background: '#27272A',
                    color: '#fff',
                    position: 'top-end',
                });
                return;
            }
        } catch (error) {
            console.error('Error finishing knowledge base review:', error);
            Swal.fire({
                title: 'Erro ao finalizar trabalho',
                text: 'Ocorreu um erro ao concluir a revisão da base de conhecimento. Tente novamente.',
                timer: 2000,
                showConfirmButton: false,
                background: '#27272A',
                color: '#fff',
                position: 'top-end',
            });
        } finally {
            setShowKnowledgeBaseReviewModal(false);
        }
    };

    return (
        <>
            <Container>
                <section id="brainstorm-concepts-header" className="mb-3">
                    <div className="d-flex flex-row align-items-center justify-content-between">
                        <h3>Conceitos gerados</h3>

                        <ButtonMain onClick={() => handleFinishBrainstorm()} className="btn btn-primary">
                            Finalizar
                        </ButtonMain>
                    </div>
                </section>
            </Container>

            <div className="brainstorm-concepts-header" />

            <section id="brainstorm-concepts-content">
                <Container>
                    <div className="row brainstorm-concepts-layout">
                        {/* Left Column - Guiding Questions */}
                        <div className="col-12 col-lg-4 brainstorm-concepts-left-column">
                            <div className="brainstorm-concepts-scrollable-content">
                                {isLoadingData ? (
                                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                                        <AnimatedBrainLoader size={100} />
                                    </div>
                                ) : (
                                    guidingQuestions.map((question, index) => {
                                        const questionIdeas = getIdeasByGuidingQuestion(currentBrainstorm?.id, question.id) || [];
                                        const selectedCount = questionIdeas.filter(idea => idea.selected === true).length;
                                        
                                        return (
                                            <div 
                                                key={question.id} 
                                                className={`problem-statement-item ${selectedGuidingQuestion === question.id ? 'selected' : ''}`}
                                                onClick={() => handleGuidingQuestionSelect(question)}
                                                role="button"
                                                tabIndex={0}
                                                aria-pressed={selectedGuidingQuestion === question.id}
                                            >
                                                <p>{question.text}</p>
                                                {selectedCount > 0 && (
                                                    <span className="selected-ideas-count">{selectedCount} conceito(s)</span>
                                                )}
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>

                        {/* Right Column - Concept Cards */}
                        <div className="col-12 col-lg-8 brainstorm-concepts-right-column">
                            <div className="brainstorm-concepts-scrollable-content">
                                {isLoadingData ? (
                                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                                        <AnimatedBrainLoader size={200} />
                                    </div>
                                ) : (
                                    !hasError && (() => {
                                        // A ordem vem do store, não da ordem de chegada dos conceitos:
                                        // é isso que mantém cada ideia na sua posição no grid.
                                        const visibleIdeas = selectedIdeas.filter(idea =>
                                            idea.concept || pendingIdeaIds.has(idea.id) || failedIdeas.has(idea.id)
                                        );

                                        if (visibleIdeas.length === 0) {
                                            return (
                                                <div className="empty-state">
                                                    <p className="text-secondary">Nenhum conceito gerado para esta questão norteadora.</p>
                                                </div>
                                            );
                                        }

                                        const pendingCount = visibleIdeas.filter(idea => !idea.concept && pendingIdeaIds.has(idea.id)).length;

                                        return (
                                            <>
                                                <div className="visually-hidden" aria-live="polite">
                                                    {pendingCount > 0 ? `Gerando ${pendingCount} conceito(s)...` : 'Geração de conceitos concluída'}
                                                </div>
                                                <div className="concepts-grid">
                                                    {visibleIdeas.map((idea) => {
                                                        if (idea.concept) {
                                                            return (
                                                                <ConceptCard
                                                                    key={idea.id}
                                                                    id={idea.id}
                                                                    ideaId={idea.id}
                                                                    title={idea.concept?.titulo_do_conceito || idea.title}
                                                                    description={idea.concept?.descricao_do_conceito || idea.description}
                                                                    conceptImageUrl={idea.concept?.signed_image || null}
                                                                    onClick={() => handleConceptCardClick(idea)}
                                                                />
                                                            );
                                                        }

                                                        if (failedIdeas.has(idea.id)) {
                                                            const failure = failedIdeas.get(idea.id) || {};

                                                            return (
                                                                <ConceptCardFailed
                                                                    key={idea.id}
                                                                    title={idea.title}
                                                                    reason={failure.reason}
                                                                    message={failure.message}
                                                                    retryable={failure.retryable}
                                                                    onRetry={onRetryConcept ? () => onRetryConcept(idea) : undefined}
                                                                />
                                                            );
                                                        }

                                                        return <ConceptCardSkeleton key={idea.id} />;
                                                    })}
                                                </div>
                                            </>
                                        );
                                    })()
                                )}
                            </div>
                        </div>
                    </div>
                </Container>
            </section>

            <ConceptDetailsModal
                show={showModal}
                onHide={handleCloseModal}
                idea={selectedIdea}
                brainstormId={currentBrainstorm?.id}
                onConceptDeleted={handleConceptDeleted}
                onConceptUpdated={handleConceptUpdated}
            />

            <KnowledgeBaseModal
                show={showKnowledgeBaseReviewModal}
                onHide={() => setShowKnowledgeBaseReviewModal(false)}
                brainstormId={currentBrainstorm?.id}
                onToggleKnowledgeBase={(brainstormId, questionId) => handleToggleKnowledgeBase(brainstormId, questionId)}
                handleConclude={handleFinishKnowledgeBaseReview}
                loading={isLoadingData || loadingConcepts || loadingKnowledgeBase}
            />
        </>
    );
}
